/**
 * In-memory micro-batch for analytics envelopes.
 *
 * Triggers:
 *  - `enqueue`: flush when ≥ MAX_BATCH events are buffered;
 *  - timer: flush when ≥ MAX_INTERVAL_MS since the last flush;
 *  - `visibilitychange`/`pagehide`: flush via `navigator.sendBeacon`
 *    (more reliable than `fetch(keepalive)` on iOS Safari).
 *
 * The queue is silent on failures: analytics never blocks UX. Drops events
 * if the backlog grows past `MAX_QUEUE_BYTES` so a long offline run cannot
 * keep growing the heap.
 */
import type { AnalyticsEnvelope } from './types'

interface QueueConfig {
  endpoint: string
  maxBatch?: number
  maxIntervalMs?: number
  maxQueueBytes?: number
}

const DEFAULTS = {
  maxBatch: 10,
  maxIntervalMs: 3000,
  maxQueueBytes: 256 * 1024,
}

export class AnalyticsQueue {
  private readonly endpoint: string
  private readonly maxBatch: number
  private readonly maxIntervalMs: number
  private readonly maxQueueBytes: number
  private buffer: AnalyticsEnvelope[] = []
  private bytesBuffered = 0
  private timer: ReturnType<typeof setTimeout> | null = null

  constructor(cfg: QueueConfig) {
    this.endpoint = cfg.endpoint.replace(/\/$/, '')
    this.maxBatch = cfg.maxBatch ?? DEFAULTS.maxBatch
    this.maxIntervalMs = cfg.maxIntervalMs ?? DEFAULTS.maxIntervalMs
    this.maxQueueBytes = cfg.maxQueueBytes ?? DEFAULTS.maxQueueBytes
  }

  enqueue(envelope: AnalyticsEnvelope): void {
    const size = approxSize(envelope)
    if (this.bytesBuffered + size > this.maxQueueBytes) {
      // Backpressure: drop the oldest half rather than blow up the heap.
      const drop = Math.ceil(this.buffer.length / 2)
      this.buffer.splice(0, drop)
      this.bytesBuffered = sumSize(this.buffer)
    }
    this.buffer.push(envelope)
    this.bytesBuffered += size
    if (this.buffer.length >= this.maxBatch) {
      this.flush('size')
      return
    }
    this.armTimer()
  }

  /** Flushes synchronously when called from unload paths via `sendBeacon`. */
  flush(reason: 'size' | 'timer' | 'unload' | 'manual' = 'manual'): void {
    if (this.buffer.length === 0) return
    this.clearTimer()
    const batch = this.buffer
    this.buffer = []
    this.bytesBuffered = 0
    const body = JSON.stringify({ events: batch })
    const url = `${this.endpoint}/events`

    if (reason === 'unload' && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      try {
        const blob = new Blob([body], { type: 'application/json' })
        const sent = navigator.sendBeacon(url, blob)
        if (sent) return
      } catch {
        // fall through to fetch
      }
    }

    try {
      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        mode: 'cors',
        credentials: 'omit',
      }).catch(() => {})
    } catch {
      // swallow — analytics is best-effort
    }
  }

  private armTimer(): void {
    if (this.timer) return
    this.timer = setTimeout(() => {
      this.timer = null
      this.flush('timer')
    }, this.maxIntervalMs)
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }
}

function approxSize(envelope: AnalyticsEnvelope): number {
  try {
    return JSON.stringify(envelope).length
  } catch {
    return 512
  }
}

function sumSize(envelopes: AnalyticsEnvelope[]): number {
  let total = 0
  for (const e of envelopes) total += approxSize(e)
  return total
}
